import { HttpClient, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { Observable, concatMap, map, merge, of } from 'rxjs';
import { FileUploadEvent } from '../modules/s3-upload/models/file-upload-event';
import { S3Response } from '../modules/s3-upload/models/s3-response';

@Injectable({ providedIn: 'root' })
export class S3UploadService {
  constructor(private http: HttpClient) {}

  /**
   * @param source
   * @param linkGenUrl
   * @param getGenUrlParams
   * @param getLinkFromResponse
   * @param callbackUrl
   * @param callbackParams
   * @deprecated It is recommended to use the s3Upload function together with the s3-upload component
   */
  upload<T extends Blob | File, L extends Record<string, unknown>, R = unknown>(
    source: T | T[],
    linkGenUrl: (f: T) => string,
    getGenUrlParams: (f: T) => HttpParams,
    getLinkFromResponse: (response: L) => string,
    callbackUrl?: (f: T) => string,
    callbackParams?: (f: T, r: L) => HttpParams
  ): Observable<R> {
    const files = _.isArray(source) ? source : [source];

    return merge(
      ...files.map(f =>
        this.generateUrl<T, L>(f, linkGenUrl, getGenUrlParams).pipe(
          concatMap(r => this.uploadToS3<T, L>(f, r, getLinkFromResponse).pipe(map(() => r))),
          concatMap((r: L) => {
            if (callbackUrl) {
              return this.http.post<R>(callbackUrl(f), null, { params: callbackParams && callbackParams(f, r) });
            }

            return of(null);
          })
        )
      )
    );
  }

  s3Upload<T extends Blob | File>(
    source: T | T[],
    linkGenUrl: (f: T) => string,
    getGenUrlParams: (f: T) => HttpParams
  ): Observable<FileUploadEvent<T>> {
    const files = _.isArray(source) ? source : [source];

    return merge(
      ...files.map(f =>
        this.s3UploadGenerateUrl<T>(f, linkGenUrl, getGenUrlParams).pipe(
          concatMap(response => {
            const req = new HttpRequest('PUT', response.uploadUrl, f, {
              headers: new HttpHeaders({ 'Content-Type': 'application/octet-stream' }),
              reportProgress: true,
            });

            return this.http.request(req).pipe(map(res => ({ ...res, ...response, file: f } as FileUploadEvent<T>)));
          })
        )
      )
    );
  }

  private s3UploadGenerateUrl<T extends File | Blob>(
    s3Data: T,
    linkGenUrl: (f: T) => string,
    getGenUrlParams: (f: T) => HttpParams
  ): Observable<S3Response> {
    return this.http.get<S3Response>(linkGenUrl(s3Data), { params: getGenUrlParams(s3Data) });
  }

  private generateUrl<T extends File | Blob, L extends Record<string, unknown>>(
    s3Data: T,
    linkGenUrl: (f: T) => string,
    getGenUrlParams: (f: T) => HttpParams
  ): Observable<L> {
    return this.http.get<L>(linkGenUrl(s3Data), { params: getGenUrlParams(s3Data) });
  }

  private uploadToS3<T extends File | Blob, L extends Record<string, unknown>>(
    s3Data: T,
    response: L,
    getLinkFromResponse: (response: L) => string
  ): Observable<void> {
    return this.http.put<void>(getLinkFromResponse(response), s3Data, {
      headers: { 'Content-Type': 'application/octet-stream' },
    });
  }
}
